import * as THREE from 'three';
import type { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

// ── Public types ──────────────────────────────────────────────────────────────

export interface TextureLoaderConfig {
  /** Shared KTX2Loader instance (re-use from GltfAssetLoader to avoid instantiating twice.
   *  Plan 10 constructs one KTX2Loader and injects the SAME instance into both loaders.). */
  readonly ktx2Loader: KTX2Loader;
}

export interface TextureAssetLoader {
  /** Load PNG or KTX2 texture from same-origin URL. Discrimination by extension:
   *  `.ktx2` → KTX2Loader; otherwise → THREE.TextureLoader.
   *  Resolves to THREE.Texture with needsUpdate=true. Rejects with TextureLoaderError. */
  load(url: string): Promise<THREE.Texture>;
}

export class TextureLoaderError extends Error {
  constructor(
    public readonly reason: 'fetch_failed' | 'decode_failed' | 'unsupported_format',
    public readonly detail: string,
    public readonly url: string,
  ) {
    super(`TextureLoaderError(${reason}, ${url}): ${detail}`);
    this.name = 'TextureLoaderError';
  }
}

// ── Internal helpers ──────────────────────────────────────────────────────────

/** Extract lowercase file extension from a URL path segment. Returns 'unknown' if none found. */
function extensionOf(url: string): 'png' | 'jpg' | 'webp' | 'ktx2' | 'unknown' {
  // Strip query/hash, grab the last path segment, extract extension
  const clean = url.split('?')[0].split('#')[0];
  const lastDot = clean.lastIndexOf('.');
  if (lastDot === -1 || lastDot === clean.length - 1) {
    return 'unknown';
  }
  const ext = clean.slice(lastDot + 1).toLowerCase();
  if (ext === 'png') return 'png';
  if (ext === 'jpg' || ext === 'jpeg') return 'jpg';
  if (ext === 'webp') return 'webp';
  if (ext === 'ktx2') return 'ktx2';
  return 'unknown';
}

/**
 * Classify a raw loader error into a typed TextureLoaderError.
 * - Message containing "fetch", "404", "403", or HTTP status codes → fetch_failed
 * - Everything else → decode_failed
 */
function classifyTextureError(err: unknown, url: string): TextureLoaderError {
  const message = err instanceof Error ? err.message : String(err);

  // HTTP status codes: 4xx and 5xx, or the word "fetch"
  const fetchPattern = /fetch|40[0-9]|4[1-9][0-9]|5[0-9]{2}/i;
  if (fetchPattern.test(message)) {
    const statusMatch = /[45][0-9]{2}/.exec(message);
    const detail = statusMatch != null ? `HTTP ${statusMatch[0]}` : message;
    return new TextureLoaderError('fetch_failed', detail, url);
  }

  return new TextureLoaderError('decode_failed', message, url);
}

// ── Factory ───────────────────────────────────────────────────────────────────

/** Factory. Constructs a TextureAssetLoader bound to the given config. */
export function createTextureAssetLoader(config: TextureLoaderConfig): TextureAssetLoader {
  return {
    load(url: string): Promise<THREE.Texture> {
      const ext = extensionOf(url);

      if (ext === 'unknown') {
        return Promise.reject(
          new TextureLoaderError('unsupported_format', `Unsupported file extension in URL: ${url}`, url),
        );
      }

      return new Promise<THREE.Texture>((resolve, reject) => {
        const onSuccess = (tex: THREE.Texture): void => {
          tex.needsUpdate = true;
          resolve(tex);
        };

        const onError = (err: unknown): void => {
          reject(classifyTextureError(err, url));
        };

        if (ext === 'ktx2') {
          config.ktx2Loader.load(url, onSuccess, undefined, onError);
        } else {
          // 'png' | 'jpg' | 'webp' — all decoded natively by the browser via THREE.TextureLoader
          new THREE.TextureLoader().load(url, onSuccess, undefined, onError);
        }
      });
    },
  };
}
